import { LocationDescriptor } from 'history';
import LinkManager from './LinkManager';
import {
    IconListItem,
    IconListItemInfo,
    MentalLinkIdentifier,
    Identifier,
    WindowLayout,
    LessonSectionLayout,
    MultipartLessonListItem,
    LessonType,
    AudioStream,
    AudioStreamType,
    MentalKnowHowTipsData,
    MentalKnowHowLink,
    IconType,
} from '../types';
import { getSlugFromTitle, getHTMLParagraphs } from './HelperFunctions';
import Config from '../Config';

function getFullUrls(urls: Array<string> | undefined): Array<string> {
    if (urls) {
        return urls.map(url => `${Config.audioLessonBaseUrl}${url}`);
    }

    return [];
}

export default class MentalApiMappingHelper {
    static mapInfoWindowLayout(mental: any): WindowLayout {
        return {
            headerColor: {
                bgColor: mental.info_window_layout.header_bg_farbe,
                fontColor: mental.info_window_layout.header_font_farbe,
            },
            headerWaveUrl: mental.info_window_layout.header_welle,
            bodyColor: {
                bgColor: mental.info_window_layout.body_bg_color,
                fontColor: mental.info_window_layout.body_font_color,
            },
        };
    }

    static mapStreamData(streamData: any): { type: AudioStreamType, urls: Array<string> } {
        let type: AudioStreamType;
        let urls: Array<string>;

        if ('voice_1' in streamData) {
            type = 'voice';

            if (streamData.voice_2) {
                urls = [streamData.voice_1, streamData.voice_2];
            } else {
                urls = [streamData.voice_1];
            }
        } else if ('music' in streamData) {
            type = 'music';
            urls = [streamData.music];
        } else if ('nature' in streamData) {
            type = 'nature';
            urls = [streamData.nature];
        } else if ('binaural' in streamData) {
            type = 'binaural';
            urls = [streamData.binaural];
        } else {
            throw new Error('Invalid stream data');
        }

        return {
            type,
            urls,
        };
    }

    static mapStreams(streamsDataHq: Array<any>, streamsDataLq: Array<any>): Array<AudioStream> {
        if (!streamsDataHq && !streamsDataLq) return [];

        const filterUrls = (urls: Array<string>) => (urls || []).filter(url => url);

        return (streamsDataLq || []).map((lqStreamData: any): AudioStream => {
            const lqTypeAndUrls = MentalApiMappingHelper.mapStreamData(lqStreamData);
            const matchingHqStream = streamsDataHq.find(
                hqStreamData => hqStreamData.title === lqStreamData.title,
            );

            let urlsHq: Array<string> = [];
            if (matchingHqStream) {
                const hqTypeAndUrls = MentalApiMappingHelper.mapStreamData(matchingHqStream);

                if (hqTypeAndUrls.type === lqTypeAndUrls.type) {
                    urlsHq = getFullUrls(filterUrls(hqTypeAndUrls.urls));
                }
            }

            return {
                active: String(lqStreamData.active) === '1',
                color: lqStreamData.color,
                title: lqStreamData.title,
                type: lqTypeAndUrls.type,
                urlsLq: getFullUrls(filterUrls(lqTypeAndUrls.urls)),
                urlsHq,
            };
        });
    }

    static mapLessonSectionLayout(layoutData: any): LessonSectionLayout {
        return {
            color: {
                bgColor: layoutData.bg_color,
                fontColor: layoutData.font_color,
            },
        };
    }

    static mentalIsLocked(mental: any): Boolean {
        return mental.mental_locked || !mental.lessons_section;
    }

    static lessonIsLocked(lesson: any): Boolean {
        if (lesson.locked) {
            return true;
        }

        let hasData = false;

        if (lesson.art === 'audio') {
            hasData = lesson.streams_hq && lesson.streams_lq;
        } else if (lesson.art === 'mehrfach') {
            hasData = lesson.lessons_section_multilesson;
        } else if (lesson.art === 'schrift') {
            hasData = lesson.text;
        } else if (lesson.art === 'video') {
            hasData = Boolean(lesson.vimeo_id);
        }

        return !hasData;
    }

    static mapLessonItems(
        mentalLinkIdentifier: MentalLinkIdentifier,
        infoDialogLayout: WindowLayout,
        infoIconColor: string,
        lessons: any,
    ): Array<IconListItem> {
        return (lessons || []).filter((lesson: any) => lesson.art === 'audio'
            || lesson.art === 'video'
            || lesson.art === 'mehrfach'
            || lesson.art === 'schrift')
            .map((lesson: any): IconListItem => {
                const locked = MentalApiMappingHelper.lessonIsLocked(lesson);
                const { id } = lesson;
                const title = lesson.titel;
                const slug = getSlugFromTitle(title);

                let itemTitle = title;
                let link: LocationDescriptor;

                if (locked) {
                    link = lesson['redirect-locked-lesson'];
                } else if (lesson.art === 'mehrfach') {
                    link = LinkManager.getMultipartLessonIndexLink(
                        {
                            mental: mentalLinkIdentifier,
                            lessonId: id,
                            lessonSlug: slug,
                        },
                    );
                } else {
                    link = LinkManager.getSingleLessonLink(
                        mentalLinkIdentifier,
                        id,
                        slug,
                    );

                    if ((lesson.art === 'audio' || lesson.art === 'video') && lesson.length) {
                        itemTitle += ` (${lesson.length})`;
                    }
                }

                let iconUrl;
                let iconType: IconType;
                if (lesson.art === 'video') {
                    iconType = MentalApiMappingHelper.lessonIsLocked(lesson)
                        ? 'icon' : 'previewImage';
                    iconUrl = lesson.preview;
                } else {
                    iconType = 'icon';
                    iconUrl = lesson.icon;
                }

                let info: IconListItemInfo | undefined;
                if (lesson.info_text) {
                    info = {
                        infoDialogLayout,
                        infoIconColor,
                        infoText: lesson.info_text,
                        title,
                    };
                }

                return {
                    id,
                    iconUrl,
                    iconType,
                    title: itemTitle,
                    link,
                    info,
                };
            });
    }

    static mapMultipartLessonItems(
        mental: MentalLinkIdentifier,
        parentLessonId: Identifier,
        parentLessonSlug: string,
        lessonsData: Array<any>,
        lockedItemRedirectUrl: string,
    ): Array<MultipartLessonListItem> {
        if (!lessonsData) return [];

        return lessonsData.map((lessonData: any): MultipartLessonListItem => {
            const { id } = lessonData;
            const title = lessonData.titel;
            const type: LessonType = lessonData.art;

            const slug = getSlugFromTitle(title);

            let length;
            if (type === 'audio' || type === 'video') {
                /* eslint-disable-next-line prefer-destructuring */
                length = lessonData.length;
            }

            const locked = MentalApiMappingHelper.lessonIsLocked(lessonData);

            const link = locked
                ? lockedItemRedirectUrl
                : LinkManager.getMultipartLessonSingleLessonLink(
                    mental,
                    parentLessonId,
                    parentLessonSlug,
                    id,
                    slug,
                );

            let iconUrl;
            let iconType: IconType;
            if (type === 'video') {
                iconType = 'previewImage';
                iconUrl = lessonData.preview;
            } else {
                iconType = 'icon';
                iconUrl = lessonData.icon;
            }

            return {
                id,
                title,
                type,
                iconColor: lessonData.icon_color,
                iconFontColor: lessonData.icon_font_color,
                iconUrl,
                iconType,
                length,
                link,
            };
        });
    }

    static mapBusiness(businessData: any): IconListItem {
        const title = 'Gesund führen';

        return {
            id: 'business',
            iconType: 'icon',
            iconUrl: businessData.icon,
            title,
            fontColor: businessData.info_icon_color,
            link: Config.pageUrls.businessGuideline,
            info: {
                infoDialogLayout: MentalApiMappingHelper.mapInfoWindowLayout(businessData),
                infoIconColor: businessData.info_icon_color,
                infoText: businessData.info_text,
                title,
            },
        };
    }

    static mapTips(mentalData: any): MentalKnowHowTipsData | undefined {
        if (!mentalData.tipps_slider) return undefined;

        return {
            tipsIconUrl: mentalData.know_how.icon,
            sliderTips: mentalData.tipps_slider.map((tip: any, id: number) => ({
                id,
                title: tip.titel,
                text: getHTMLParagraphs(tip.text),
            })),
        };
    }

    static mapKnowHowTextLinks(
        mentalLinkIdentifier: MentalLinkIdentifier,
        mentalData: any,
    ): Array<MentalKnowHowLink> | undefined {
        if (!mentalData.tipps_long) return undefined;

        return mentalData.tipps_long.map((longTip: any, index: number): MentalKnowHowLink => {
            const title = longTip.titel;
            const slug = getSlugFromTitle(title);

            return {
                id: index,
                link: LinkManager.getKnowHowTextLink(mentalLinkIdentifier, index + 1, slug),
                title,
            };
        });
    }
}
